import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';

import { authenticationAtom } from '../State';
import { idAsString, loadProjects } from './projectSites';

/**
 * Which project the CBOS screens are looking at.
 *
 * One choice for the whole area rather than one per screen. Before this, the dashboard and the
 * heatmap each carried their own picker and their own idea of the answer: switching screens meant
 * choosing again, and the two could disagree about which project was on the table — which is not a
 * disagreement anybody wants to have with a piece of software.
 *
 * The answer lives in the address, `?project=`. That is what makes it survive a screen change, a
 * reload and a link sent to a colleague; component state would survive none of the three. The
 * dashboard already read `?project=`, so the address was half the truth already and is now all of
 * it.
 */

/**
 * The tenant's projects, remembered for as long as the tab lives.
 *
 * The picker sits in the sidebar and the screens ask for the same list; without this each of them
 * would fetch it, and a screen change would fetch it again. A project list changes when somebody
 * creates a project, which is not something this page does.
 */
const cache = new Map<string, Promise<any[]>>();

export const projectsOf = (customer: string): Promise<any[]> => {
    const known = cache.get(customer);
    if (known) return known;

    const loading = loadProjects(customer).catch((error) => {
        // Not kept: a failed load must not become the permanent answer for the rest of the session.
        cache.delete(customer);
        throw error;
    });

    cache.set(customer, loading);

    return loading;
};

/**
 * Wer erfahren will, dass die Projekte neu zu lesen sind.
 *
 * Den Zwischenspeicher zu leeren genügt nicht. Eine Ansicht, die schon steht, hat ihre Liste im
 * Zustand und liest nur beim Mandantenwechsel nach — sie erführe vom Vergessen nichts und zeigte
 * den Stand von vorhin, bis jemand die Seite wechselt. Genau das war zu sehen, nachdem eine
 * Liegenschaft aus dem Datenraum heraus ergänzt wurde: Der Datenraum wusste davon, die
 * Liegenschaftsliste der Heatmap nicht.
 */
const listeners = new Set<() => void>();

export const onProjectsChanged = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/**
 * After a project is created or renamed elsewhere — oder wenn ihm eine Liegenschaft zuwächst.
 *
 * Erst vergessen, dann Bescheid sagen: Ein Zuhörer, der sofort nachliest, muss den leeren
 * Zwischenspeicher vorfinden, sonst bekäme er noch einmal die alte Antwort.
 */
export const forgetProjects = (customer?: string) => {
    if (customer) cache.delete(customer);
    else cache.clear();

    listeners.forEach((listener) => {
        try {
            listener();
        } catch (error) {
            // Ein Zuhörer, der stolpert, darf die übrigen nicht mitreißen.
            console.error('A projects listener threw', error);
        }
    });
};

export interface SelectedProject {
    projectId: string | null;
    /** The project itself, once the list has arrived. */
    project: any | null;
    projects: any[];
    loading: boolean;
    select: (projectId: string | null) => void;
}

export const useSelectedProject = (): SelectedProject => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [authentication] = useAtom(authenticationAtom) as any;

    const [projects, setProjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    /** Hochgezählt, wenn die Projekte vergessen wurden — das ist der Anlass zum Nachlesen. */
    const [revision, setRevision] = useState(0);

    useEffect(() => onProjectsChanged(() => setRevision((current) => current + 1)), []);

    const customer = authentication?.customer;
    const projectId = searchParams.get('project');

    useEffect(() => {
        if (!customer) {
            setProjects([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        projectsOf(customer)
            .then((found) => {
                if (!cancelled) setProjects(found ?? []);
            })
            .catch((error: any) => console.error('Could not load the projects', error))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [customer, revision]);

    /**
     * Choosing a project drops what belonged to the old one.
     *
     * A site id is a site *of a project*; carried across it would point at something the new
     * project does not contain, and the screen would show one project's name over another's
     * property.
     */
    const select = useCallback(
        (next: string | null) =>
            setSearchParams(
                (current) => {
                    const parameters = new URLSearchParams(current);

                    if (next) parameters.set('project', next);
                    else parameters.delete('project');

                    parameters.delete('site');

                    return parameters;
                },
                { replace: false },
            ),
        [setSearchParams],
    );

    return {
        projectId,
        project: projectId ? (projects.find((entry) => idAsString(entry._id) === projectId) ?? null) : null,
        projects,
        loading,
        select,
    };
};
